import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../utils/roles';
import ContactModal from '../components/ContactModal';
import DataTable from '../components/DataTable';
import './ContactsPage.css';

const ContactsPage = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [centers, setCenters] = useState([]); 
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // MODAL STATE
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    if (user) {
      fetchContacts();
      if (user.role === ROLES.SC_ADMIN && user.scId) {
        fetchSchoolInfo(user.scId);
      }
      if (user.role === ROLES.ADMIN) {
        fetchCenters();
      }
    }
  }, [user]);

  const fetchCenters = async () => {
    try {
      const res = await apiClient.get('/api/sprogcentre');
      setCenters(res.data);
    } catch(err) { console.error(err); }
  };

  const fetchSchoolInfo = async (scId) => {
    try {
      const res = await apiClient.get(`/api/sprogcentre/${scId}`);
      setSchoolInfo(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      let url = '/api/kontakter';
      if (user.role === 'SC_ADMIN' && user.scId) {
        url += `?scId=${user.scId}`;
      }
      const response = await apiClient.get(url);
      setContacts(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Fejl', err);
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingContact(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (contact) => {
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (data.id) {
        await apiClient.put(`/api/kontakter/${data.id}`, data);
        setContacts(prev => prev.map(c => c.id === data.id ? { ...c, ...data } : c));
      } else {
        const res = await apiClient.post('/api/kontakter', data);
        const scName = centers.find(sc => sc.id === parseInt(data.sprogcenter_id))?.navn || schoolInfo?.navn || 'Nyt';
        const newContact = { ...res.data, sprogcenter_navn: scName };
        setContacts(prev => [...prev, newContact]);
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert('Der skete en fejl. Tjek dine rettigheder.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/api/kontakter/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert('Du har ikke rettigheder til at slette denne person.');
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const search = searchTerm.toLowerCase();
    const nameMatch = contact.navn?.toLowerCase().includes(search);
    const roleMatch = contact.rolle?.toLowerCase().includes(search);
    const scMatch = contact.sprogcenter_navn?.toLowerCase().includes(search);
    return nameMatch || roleMatch || scMatch;
  });

  // 2. DEFINER KOLONNER TIL DATATABLE
  const columns = [
    {
      header: 'Navn',
      accessor: 'navn', // Gør det muligt at sortere
      width: '22%',
      render: (row) => <strong>{row.navn}</strong>
    },
    {
      header: 'Rolle',
      accessor: 'rolle',
      width: '18%',
      render: (row) => <span className="role-badge">{row.rolle}</span>
    },
    {
      header: 'Sprogcenter',
      accessor: 'sprogcenter_navn',
      width: '22%',
      render: (row) => <span style={{color: '#003366', fontWeight: 500}}>{row.sprogcenter_navn}</span>
    },
    {
      header: 'Kontaktinfo',
      accessor: 'email', // Sorterer på email
      width: '25%',
      render: (row) => (
        <div>
          <div>{row.email}</div>
          <div style={{color:'#666', fontSize:'0.8rem'}}>{row.telefon}</div>
        </div>
      )
    },
    {
      header: 'Handling',
      width: '13%',
      align: 'right',
      render: (row) => (
        <button 
          onClick={() => handleOpenEdit(row)}
          className="edit-btn"
        >
          Rediger
        </button>
      )
    }
  ];

  return (
    <div>

      {schoolInfo && (
        <div className="school-info-card">
          <div>
            <div className="school-info-title">{schoolInfo.navn}</div>
            <div className="school-info-details">
              <div className="info-item"><span className="info-label">Adresse</span><span>{schoolInfo.adresse}, {schoolInfo.postnummer} {schoolInfo.forstad}</span></div>
              <div className="info-item"><span className="info-label">Kontakt</span><span>{schoolInfo.email} | Tlf: {schoolInfo.telefon}</span></div>
              <div className="info-item"><span className="info-label">Organisation</span><span>{schoolInfo.organisations_navn || '-'}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* 3. INDSÆT DET NYE LAYOUT */}
      <div className="table-card">
        
        {/* Header Content (Søg + Knap) */}
        <div className="card-header-content">
          <div className="filter-bar" style={{display: 'flex', justifyContent: 'space-between', marginBottom: 0}}>
            <input
              type="text"
              className="search-input"
              placeholder="Søg på navn, rolle eller sprogcenter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{padding: '10px', width: '350px', border: '1px solid #ddd', borderRadius: '8px'}}
            />
            
            <button
              onClick={handleOpenCreate}
              style={{backgroundColor: '#003366', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer'}}
            >
              + Opret Ny Kontakt
            </button>
          </div>
        </div>

        {/* DataTable Komponenten */}
        <DataTable
          columns={columns}
          data={filteredContacts}
          loading={loading}
          noDataMessage="Ingen kontaktpersoner fundet."
          initialSortKey="navn"
        />
      </div>

      <ContactModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
        contact={editingContact}
        user={user}
        centers={centers}
      />
    </div>
  );
};

export default ContactsPage;